import {
  Building2,
  CircleDollarSign,
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  FileBarChart,
  FolderUp,
  Globe,
  GraduationCap,
  HandCoins,
  HelpCircle,
  LayoutDashboard,
  PenLine,
  Video,
  Megaphone,
  Receipt,
  ReceiptText,
  Settings2,
  ShieldCheck,
  Star,
  Target,
  UserPlus,
  Users,
  CalendarCheck,
  Wallet,
  Wrench,
} from 'lucide-react';
import type { NavGroup, PaletteCommand } from './types';

/**
 * Center owner / manager app. Organised by job, not by feature:
 * what needs me → teach and track → who is enrolled → get paid → fill batches → talk to parents → configure.
 * Sub-sections of an item are tabs inside its page, never a second sidebar.
 */
export const ownerNav: readonly NavGroup[] = [
  {
    id: 'today',
    items: [{ id: 'today', label: 'Today', to: '/app', icon: LayoutDashboard, end: true, tab: true, keywords: ['home', 'dashboard', 'overview'] }],
  },
  {
    id: 'academics',
    label: 'Academics',
    items: [
      { id: 'batches', label: 'Batches', to: '/app/batches', icon: GraduationCap, tab: true, keywords: ['class', 'roster', 'jee', 'neet', 'foundation', 'timetable'] },
      { id: 'material', label: 'Study material', to: '/app/material', icon: FolderUp, keywords: ['notes', 'upload', 'dpp', 'pdf', 'share', 'download'] },
      { id: 'tests', label: 'Tests', to: '/app/tests', icon: ClipboardCheck, keywords: ['exam', 'marks', 'results', 'mock', 'rank'] },
      { id: 'lectures', label: 'Lectures', to: '/app/lectures', icon: Video, keywords: ['live class', 'recording', 'video', 'schedule class'] },
      { id: 'quizzes', label: 'Practice quizzes', to: '/app/quizzes', icon: PenLine, keywords: ['mcq', 'quiz', 'practice', 'questions'] },
      { id: 'homework', label: 'Homework', to: '/app/homework', icon: ClipboardList, keywords: ['assignment', 'dpp'] },
      { id: 'doubts', label: 'Doubts', to: '/app/doubts', icon: HelpCircle, keywords: ['questions', 'answer', 'students'] },
    ],
  },
  {
    id: 'operate',
    label: 'Operate',
    items: [
      { id: 'students', label: 'Students', to: '/app/students', icon: Users, tab: true, keywords: ['students', 'people', 'withdrawn', 'parents'] },
      { id: 'center', label: 'Classrooms', to: '/app/center', icon: Building2, keywords: ['rooms', 'halls', 'capacity', 'seats', 'floors'] },
      { id: 'issues', label: 'Issues', to: '/app/issues', icon: Wrench, keywords: ['complaints', 'support', 'tickets', 'sla'] },
    ],
  },
  {
    id: 'money',
    label: 'Money',
    items: [
      { id: 'dues', label: 'Fees', to: '/app/dues', icon: Receipt, tab: true, tabLabel: 'Fees', keywords: ['fee', 'dues', 'collect payment', 'overdue', 'invoice'] },
      { id: 'collected', label: 'Collected', to: '/app/collected', icon: HandCoins, keywords: ['payments', 'receipts', 'collections'] },
      { id: 'expenses', label: 'Expenses', to: '/app/expenses', icon: ReceiptText, keywords: ['spend', 'vendor', 'voucher', 'salary'] },
      { id: 'reports', label: 'Reports', to: '/app/reports', icon: FileBarChart, keywords: ['export', 'p&l', 'ledger', 'statements'] },
    ],
  },
  {
    id: 'growth',
    label: 'Growth',
    items: [
      { id: 'leads', label: 'Enquiries', to: '/app/leads', icon: Target, keywords: ['leads', 'prospects', 'walk-in', 'pipeline', 'demo class'] },
      { id: 'admissions', label: 'Admissions', to: '/app/admissions', icon: CalendarCheck, keywords: ['token', 'enrol', 'join', 'registration'] },
      { id: 'listings', label: 'Listings', to: '/app/listings', icon: Globe, keywords: ['microsite', 'marketing', 'photos'] },
      { id: 'reviews', label: 'Reviews', to: '/app/reviews', icon: Star, keywords: ['surveys', 'feedback', 'ratings'] },
    ],
  },
  {
    id: 'communicate',
    label: 'Parents',
    items: [{ id: 'communicate', label: 'Communicate', to: '/app/communicate', icon: Megaphone, keywords: ['announcements', 'whatsapp', 'broadcast', 'parents', 'notices', 'ptm'] }],
  },
  {
    id: 'admin',
    label: 'Admin',
    items: [
      { id: 'team', label: 'Team', to: '/app/team', icon: ShieldCheck, keywords: ['staff', 'teachers', 'permissions', 'roles'] },
      { id: 'setup', label: 'Setup', to: '/app/setup', icon: Settings2, keywords: ['settings', 'rules', 'fee defaults', 'sla'] },
      { id: 'billing', label: 'Billing', to: '/app/billing', icon: CreditCard, keywords: ['subscription', 'wallet', 'bank', 'plan'] },
    ],
  },
];

/** "+ New" and palette shortcuts. Each destination is a real route that opens the right form. */
export const ownerCommands: readonly PaletteCommand[] = [
  { id: 'new-student', label: 'Add student', to: '/app/students?new=1', icon: UserPlus, keywords: ['onboard', 'student', 'new', 'admission'] },
  { id: 'new-lead', label: 'Add enquiry', to: '/app/leads?new=1', icon: Target, keywords: ['lead', 'walk-in'] },
  { id: 'collect', label: 'Collect payment', to: '/app/dues?collect=1', icon: Wallet, keywords: ['receive', 'fee', 'cash', 'upi'] },
  { id: 'new-due', label: 'Add fee', to: '/app/dues?new=1', icon: CircleDollarSign, keywords: ['charge', 'invoice', 'instalment'] },
  { id: 'new-expense', label: 'Add expense', to: '/app/expenses?new=1', icon: ReceiptText, keywords: ['spend', 'voucher'] },
  { id: 'share-material', label: 'Share study material', to: '/app/material', icon: FolderUp, keywords: ['notes', 'upload', 'pdf'] },
  { id: 'new-announcement', label: 'Send announcement', to: '/app/communicate?new=1', icon: Megaphone, keywords: ['broadcast', 'notice', 'whatsapp', 'parents'] },
];
